import { useEffect, useRef, type FC, type ReactNode } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useToast } from "@chakra-ui/react";
import { useAuthStore, type AuthState } from "stores/authStore";

type ProtectedRouteProps = {
  permission: string;
  children?: ReactNode;
};

/**
 * Protege rutas por permiso (estado /auth/me/). Sin permiso: vuelve atrás y avisa.
 */
export const PermissionProtectedRoute: FC<ProtectedRouteProps> = ({
  permission,
  children,
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const hasPermission = useAuthStore((s: AuthState) => s.hasPermission(permission));
  const meStatus = useAuthStore((s: AuthState) => s.meStatus);
  const me = useAuthStore((s: AuthState) => s.me);
  const warned = useRef(false);

  useEffect(() => {
    if (meStatus !== "ready") {
      return;
    }
    if (me === null) {
      return;
    }
    if (!hasPermission && !warned.current && !me?.is_staff) {
      warned.current = true;
      toast({
        title: `No tienes el permiso ${permission}`,
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      navigate(-1);
    }
  }, [meStatus, me, hasPermission, permission, navigate, toast]);

  if (meStatus !== "ready" || me === null) {
    return null;
  }

  if (!hasPermission && !me?.is_staff) {
    return null;
  }

  return <>{children ?? <Outlet />}</>;
};
